import { wrapCreateBrowserRouterV6 } from '@sentry/react'
import type { QueryClient } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { createBrowserRouter, Navigate, RouterProvider, Outlet, ScrollRestoration } from 'react-router-dom'

import {
  AuthGuard,
  ConsumerAuthGuard,
  EnterpriseAuthGuard,
  EnterpriseSignInFlowGuard,
} from 'src/app/router/guards/auth'
import {
  EnterpriseOnboardingPageGuard,
  EnterpriseOnboardingGuard,
  OnboardingGuard,
} from 'src/app/router/guards/onboarding'
import AuthLayout from 'src/app/router/layouts/auth-layout'
import PublicLayout from 'src/app/router/layouts/public-layout'
import ReceivedReferralSharePage from 'src/app/router/routes/business/referrals/detail/received-referral-share'
import SentReferralPage from 'src/app/router/routes/business/referrals/detail/sent-referral'
import ErrorPage from 'src/app/router/routes/error'
import UserProfile from 'src/app/router/routes/user/profile'
import { paths } from 'src/config/paths'
import BusinessCreateLayout from 'src/features/business-create/layouts/business-create-layout'
import ChatsLayout from 'src/features/chats/layouts/chats-layout'
import SettingsLayout from 'src/features/settings/layouts/settings-layout'
import { AppErrorBoundary } from 'src/shared/components/errors/app-error-boundary'
import { ClientErrorBoundary } from 'src/shared/components/errors/client-error-boundary'
import { ErrorBoundaryWrapper } from 'src/shared/components/errors/error-boundary-wrapper'
import { GenericSuspense } from 'src/shared/components/ui/miscellaneous/generic-suspense'
import EnterpriseAuthLayout from 'src/shared/features/enterprise/auth/layout'
import EnterpriseSettingsLayout from 'src/shared/features/enterprise/components/dashboard/settings/enterprise-settings-layout'
import { usePageView } from 'src/shared/hooks/use-page-view'
import { useTrackingParams } from 'src/shared/hooks/use-tracking-params'

import BlockEnterpriseGuard from './guards/block-enterprise-guard'
import BlockWebGuard from './guards/block-web-guard'
// import ShowMFAGuard from './guards/show-mfa-guard'
import AuthenticatedLayout from './layouts/authenticated-layout'
import ConsumerAuthLayout from './layouts/consumer-auth-layout'
import { EnterpriseDashboardLayout } from './layouts/enterprise-layout'
// import ProfessionalPublicLayout from './layouts/professional-public-layout'
// import ProfessionalPublicLayout from './layouts/professional-public-layout'
import PublicProfileLayout from './layouts/public-profile-layout'
import AutoPopupProvider from './providers/auto-popup-provider'
import { EnterpriseHeadProvider } from './providers/enterprise-head-provider'
import BusinessLayout from './routes/business/layout/business-layout'

const convert =
  (queryClient: QueryClient, suspense = false) =>
  (m: any) => {
    const { clientLoader, clientAction, default: Component, ...rest } = m
    const WrappedComponent = suspense
      ? (props: any) => (
          <GenericSuspense style={props.style || 'fullscreen'}>
            <Component {...props} />
          </GenericSuspense>
        )
      : Component

    return {
      ...rest,
      loader: clientLoader?.(queryClient),
      action: clientAction?.(queryClient),
      Component: WrappedComponent,
    }
  }

const RootLogic = () => {
  usePageView()
  // Initialize tracking parameters (GCLID and UTM)
  useTrackingParams()

  return (
    <>
      <Outlet />
      <ScrollRestoration />
    </>
  )
}

export const createAppRouter = (queryClient: QueryClient): ReturnType<typeof createBrowserRouter> => {
  const sentryCreateBrowserRouter = wrapCreateBrowserRouterV6(createBrowserRouter)

  return sentryCreateBrowserRouter([
    {
      // Root Logic -> Log page views and transition animations
      element: (
        <ErrorBoundaryWrapper>
          <AutoPopupProvider>
            <RootLogic />
          </AutoPopupProvider>
        </ErrorBoundaryWrapper>
      ),
      errorElement: <ErrorPage />,
      children: [
        // Auth & Onboarding
        {
          path: paths.auth.oauthCallback.path,
          lazy: () => import('./routes/auth/oauth-callback').then(convert(queryClient)),
        },
        {
          path: paths.auth.oauthLogout.path,
          lazy: () => import('./routes/auth/oauth-logout').then(convert(queryClient)),
        },
        {
          path: paths.auth.oauthMfaVerify.path,
          lazy: () => import('./routes/auth/oauth-mfa-verify').then(convert(queryClient)),
        },
        {
          element: <AuthLayout />,
          children: [
            {
              path: paths.auth.login.path,
              lazy: () => import('./routes/auth/professional/login').then(convert(queryClient)),
            },
            {
              path: paths.auth.loginPhone.path,
              lazy: () => import('./routes/auth/professional/login-phone').then(convert(queryClient)),
            },
            {
              path: paths.auth.signup.path,
              // Signup is the same as login for agents & LOs. Kept for backwards compatibility.
              lazy: () => import('./routes/auth/professional/login').then(convert(queryClient)),
            },
            {
              path: paths.auth.onboarding.path,
              lazy: () => import('./routes/auth/onboarding').then(convert(queryClient)),
            },
          ],
        },

        // Under Construction
        {
          children: [
            {
              path: paths.alert.path,
              lazy: () => import('./routes/alert/index').then(convert(queryClient)),
            },
          ],
        },

        // Consumer Auth
        {
          element: <ConsumerAuthLayout />,
          children: [
            {
              path: paths.consumer.auth.login.path,
              lazy: () => import('./routes/auth/consumer/login').then(convert(queryClient)),
            },
            {
              path: paths.consumer.auth.signup.path,
              lazy: () => import('./routes/auth/consumer/signup').then(convert(queryClient)),
            },
            {
              path: paths.consumer.auth.loginPhone.path,
              lazy: () => import('./routes/auth/consumer/login-phone').then(convert(queryClient)),
            },
          ],
        },

        // Consumer Authenticated Pages
        {
          element: (
            <ClientErrorBoundary>
              <GenericSuspense style="fullscreen">
                <ConsumerAuthGuard>
                  <Outlet />
                </ConsumerAuthGuard>
              </GenericSuspense>
            </ClientErrorBoundary>
          ),
          children: [
            {
              path: paths.client.index.path,
              lazy: () => import('./routes/client/index').then(convert(queryClient)),
            },
            {
              path: paths.client.business.path,
              lazy: () => import('./routes/client/business').then(convert(queryClient)),
            },
          ],
        },

        // Enterprise Auth
        {
          element: (
            <EnterpriseSignInFlowGuard>
              <EnterpriseAuthLayout variant="default">
                <Outlet />
              </EnterpriseAuthLayout>
            </EnterpriseSignInFlowGuard>
          ),
          children: [
            {
              path: paths.enterprise.login.path,
              lazy: () => import('./routes/auth/enterprise/login').then(convert(queryClient)),
            },
            {
              path: paths.enterprise.loginPhone.path,
              lazy: () => import('./routes/auth/enterprise/login-phone').then(convert(queryClient)),
            },
          ],
        },

        // Enterprise Onboarding
        {
          element: (
            <EnterpriseHeadProvider>
              <EnterpriseAuthGuard>
                <EnterpriseOnboardingPageGuard>
                  <EnterpriseAuthLayout>
                    <Outlet />
                  </EnterpriseAuthLayout>
                </EnterpriseOnboardingPageGuard>
              </EnterpriseAuthGuard>
            </EnterpriseHeadProvider>
          ),
          children: [
            {
              path: paths.enterprise.onboarding.path,
              lazy: () => import('./routes/auth/enterprise/onboarding').then(convert(queryClient)),
            },
          ],
        },

        // Enterprise Home Public Page
        {
          element: (
            <EnterpriseHeadProvider>
              <Outlet />
            </EnterpriseHeadProvider>
          ),
          children: [
            {
              path: paths.enterprise.path,
              lazy: () => import('./routes/enterprise/index').then(convert(queryClient)),
              errorElement: (
                <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />
              ),
            },
            // Duplicate route for backwards compatibility. The professionals route is rerouted to the enterprise pages.
            {
              path: paths.professionals.index.path,
              lazy: () => import('./routes/enterprise/index').then(convert(queryClient)),
              errorElement: (
                <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />
              ),
            },
            {
              path: paths.enterprise.solutions.path,
              lazy: () => import('./routes/enterprise/solutions/index').then(convert(queryClient)),
            },
            {
              path: paths.enterprise.solutions.smallCompanies.path,
              lazy: () => import('./routes/enterprise/solutions/small-companies').then(convert(queryClient)),
            },
            {
              path: paths.enterprise.solutions.midMarkets.path,
              lazy: () => import('./routes/enterprise/solutions/mid-markets').then(convert(queryClient)),
            },
            {
              path: paths.enterprise.solutions.enterprises.path,
              lazy: () => import('./routes/enterprise/solutions/enterprises').then(convert(queryClient)),
            },
            {
              path: paths.enterprise.switchToConrad.path,
              lazy: () => import('./routes/enterprise/switch-to-conrad/index').then(convert(queryClient)),
            },
            {
              path: paths.enterprise.howItWorks.path,
              lazy: () => import('./routes/enterprise/how-it-works').then(convert(queryClient)),
            },
            {
              path: paths.enterprise.securityAndCompliance.path,
              lazy: () => import('./routes/enterprise/security-and-compliance').then(convert(queryClient)),
            },
            {
              path: paths.enterprise.aboutUs.path,
              lazy: () => import('./routes/enterprise/about-us').then(convert(queryClient)),
            },
          ],
        },

        // Professional Public Pages
        // Hide for now. May delete later.
        // {
        //   element: <ProfessionalPublicLayout />,
        //   children: [
        //     {
        //       path: paths.professionals.index.path,
        //       lazy: () => import('./routes/professionals/index').then(convert(queryClient)),
        //       errorElement: (
        //         <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />
        //       ),
        //     },
        //     {
        //       path: paths.professionals.overview.path,
        //       lazy: () => import('./routes/professionals/overview').then(convert(queryClient)),
        //     },
        //     {
        //       path: paths.professionals.integrations.path,
        //       lazy: () => import('./routes/professionals/integrations').then(convert(queryClient)),
        //     },
        //     {
        //       path: paths.professionals.solutionOne.path,
        //       lazy: () => import('./routes/professionals/solution-one').then(convert(queryClient)),
        //     },
        //     {
        //       path: paths.professionals.implementAi.path,
        //       lazy: () => import('./routes/professionals/implement-ai').then(convert(queryClient)),
        //     },
        //     {
        //       path: paths.professionals.growYourNetwork.path,
        //       lazy: () => import('./routes/professionals/grow-your-network').then(convert(queryClient)),
        //     },
        //     {
        //       path: paths.professionals.convertMoreLeads.path,
        //       lazy: () => import('./routes/professionals/convert-more-leads').then(convert(queryClient)),
        //     },
        //     {
        //       path: paths.professionals.runYourBusiness.path,
        //       lazy: () => import('./routes/professionals/run-your-business').then(convert(queryClient)),
        //     },
        //     {
        //       path: paths.professionals.getMoreBusiness.path,
        //       lazy: () => import('./routes/professionals/get-more-business').then(convert(queryClient)),
        //     },
        //   ],
        // },

        // Enterprise Authenticated Pages
        {
          element: (
            <EnterpriseHeadProvider>
              <EnterpriseAuthGuard>
                <EnterpriseOnboardingGuard>
                  <BlockEnterpriseGuard>
                    <Outlet />
                  </BlockEnterpriseGuard>
                </EnterpriseOnboardingGuard>
              </EnterpriseAuthGuard>
            </EnterpriseHeadProvider>
          ),
          children: [
            // MFA Setup Page using Enterprise Auth Layout
            {
              element: (
                <EnterpriseAuthLayout>
                  <Outlet />
                </EnterpriseAuthLayout>
              ),
              children: [
                {
                  path: paths.enterprise.mfaSetup.path,
                  lazy: () => import('./routes/auth/enterprise/mfa').then(convert(queryClient)),
                },
              ],
            },
            // Enterprise Dashboard Page using Enterprise Dashboard Layout
            {
              element: (
                // <ShowMFAGuard mfaRoute={paths.enterprise.mfaSetup.path}>
                <EnterpriseDashboardLayout>
                  <ErrorBoundaryWrapper>
                    <Outlet />
                  </ErrorBoundaryWrapper>
                </EnterpriseDashboardLayout>
                // </ShowMFAGuard>
              ),
              children: [
                {
                  path: paths.enterprise.dashboard.path,
                  lazy: () => import('./routes/enterprise/dashboard/index').then(convert(queryClient)),
                },
                // HIDE THESE ROUTES FOR NOW
                // {
                //   path: paths.enterprise.dashboard.overview.path,
                //   lazy: () => import('./routes/enterprise/dashboard/overview/index').then(convert(queryClient)),
                // },
                {
                  path: paths.enterprise.dashboard.directory.path,
                  lazy: () => import('./routes/enterprise/dashboard/directory/index').then(convert(queryClient)),
                },
                {
                  path: paths.enterprise.dashboard.pipeline.path,
                  lazy: () => import('./routes/enterprise/dashboard/pipeline/index').then(convert(queryClient)),
                },
                {
                  path: paths.enterprise.dashboard.settings.path,
                  element: <EnterpriseSettingsLayout />,
                  children: [
                    {
                      index: true,
                      lazy: () => import('./routes/enterprise/dashboard/settings/index').then(convert(queryClient)),
                    },
                    {
                      path: paths.enterprise.dashboard.settings.personalInfo.path,
                      lazy: () =>
                        import('./routes/enterprise/dashboard/settings/personal-info').then(convert(queryClient)),
                    },
                    {
                      path: paths.enterprise.dashboard.settings.companyInfo.path,
                      lazy: () => import('./routes/enterprise/dashboard/settings/index').then(convert(queryClient)),
                    },
                    {
                      path: paths.enterprise.dashboard.settings.members.path,
                      lazy: () => import('./routes/enterprise/dashboard/settings/index').then(convert(queryClient)),
                    },
                    {
                      path: paths.enterprise.dashboard.settings.integrations.path,
                      lazy: () => import('./routes/enterprise/dashboard/settings/index').then(convert(queryClient)),
                    },
                    {
                      path: paths.enterprise.dashboard.settings.workflows.path,
                      lazy: () => import('./routes/enterprise/dashboard/settings/workflows').then(convert(queryClient)),
                    },
                    {
                      path: paths.enterprise.dashboard.settings.leadDistribution.path,
                      lazy: () =>
                        import('./routes/enterprise/dashboard/settings/lead-distribution').then(convert(queryClient)),
                    },
                    {
                      path: paths.enterprise.dashboard.settings.api.path,
                      lazy: () => import('./routes/enterprise/dashboard/settings/api').then(convert(queryClient)),
                    },
                    {
                      path: paths.enterprise.dashboard.settings.logs.path,
                      lazy: () => import('./routes/enterprise/dashboard/settings/logs').then(convert(queryClient)),
                    },
                    {
                      path: paths.enterprise.dashboard.settings.security.path,
                      lazy: () => import('./routes/enterprise/dashboard/settings/index').then(convert(queryClient)),
                    },
                    {
                      path: paths.enterprise.dashboard.settings.terms.path,
                      lazy: () => import('./routes/enterprise/dashboard/settings/terms').then(convert(queryClient)),
                    },
                    {
                      path: paths.enterprise.dashboard.settings.privacy.path,
                      lazy: () => import('./routes/enterprise/dashboard/settings/privacy').then(convert(queryClient)),
                    },
                  ],
                },
                // HIDE THESE ROUTES FOR NOW
                // {
                //   path: paths.enterprise.dashboard.billing.path,
                //   lazy: () => import('./routes/enterprise/dashboard/billing/index').then(convert(queryClient)),
                // },
                // {
                //   path: paths.enterprise.dashboard.activity.path,
                //   lazy: () => import('./routes/enterprise/dashboard/activity/index').then(convert(queryClient)),
                // },
              ],
            },
          ],
        },

        // TEMP DELETE ACCOUNT PAGE
        {
          element: (
            <ErrorBoundaryWrapper style="custom" fallback={<AppErrorBoundary />}>
              <Outlet />
            </ErrorBoundaryWrapper>
          ),
          children: [
            {
              path: paths.deleteAccount.path,
              lazy: () => import('./routes/delete-account/index').then(convert(queryClient)),
            },
            {
              element: (
                <AuthGuard>
                  <Outlet />
                </AuthGuard>
              ),
              children: [
                {
                  path: paths.deleteAccount.web.path,
                  lazy: () => import('./routes/delete-account/delete-account-page').then(convert(queryClient)),
                },
              ],
            },
          ],
        },

        // Download App Page
        {
          path: paths.downloadApp.path,
          lazy: () => import('./routes/download-app/index').then(convert(queryClient)),
        },

        // Privacy Policy Page
        {
          path: paths.privacyPolicy.path,
          lazy: () => import('./routes/privacy-policy/index').then(convert(queryClient)),
        },

        // Terms of Services Page
        {
          path: paths.termsOfService.path,
          lazy: () => import('./routes/terms-of-service/index').then(convert(queryClient)),
        },

        // Authenticated Pages
        {
          element: (
            <ErrorBoundaryWrapper style="custom" fallback={<AppErrorBoundary />}>
              <BlockWebGuard>
                <AuthGuard>
                  <OnboardingGuard>
                    <Outlet />
                  </OnboardingGuard>
                </AuthGuard>
              </BlockWebGuard>
            </ErrorBoundaryWrapper>
          ),
          children: [
            // MFA Setup Page using Auth Layout
            {
              element: <Outlet />,
              children: [
                {
                  path: paths.auth.mfaSetup.path,
                  lazy: () => import('./routes/auth/professional/mfa').then(convert(queryClient)),
                },
              ],
            },
            // Default Layout (Show both header and footer)
            {
              element: <AuthenticatedLayout />,
              children: [
                {
                  path: paths.home.path,
                  lazy: () => import('./routes/home').then(convert(queryClient)),
                },
                {
                  path: paths.search.path,
                  lazy: () => import('./routes/search').then(convert(queryClient)),
                },
                {
                  path: paths.feed.path,
                  lazy: () => import('./routes/feed/timeline').then(convert(queryClient)),
                },
                {
                  path: paths.feed.post.create.path,
                  lazy: () => import('./routes/feed/create-post').then(convert(queryClient)),
                },
                {
                  path: paths.referrals.getMatched.path,
                  element: <Navigate to={paths.home.getHref()} replace />,
                },
                {
                  path: paths.settings.path,
                  element: <SettingsLayout />,
                  children: [
                    {
                      index: true,
                      lazy: () => import('./routes/settings/index').then(convert(queryClient)),
                    },
                    {
                      path: 'personal-info',
                      lazy: () => import('./routes/settings/personal-info-settings-page').then(convert(queryClient)),
                    },
                    {
                      path: 'profile',
                      lazy: () => import('./routes/settings/account-preferences-page').then(convert(queryClient)),
                    },
                    {
                      path: 'verify-email',
                      lazy: () => import('./routes/settings/verify-email-page').then(convert(queryClient)),
                    },
                    {
                      path: 'security',
                      lazy: () => import('./routes/settings/security-settings-page').then(convert(queryClient)),
                    },
                    {
                      path: 'security/mfa',
                      lazy: () => import('./routes/settings/mfa-page').then(convert(queryClient)),
                    },
                    {
                      path: 'notifications',
                      lazy: () => import('./routes/settings/notification-settings-page').then(convert(queryClient)),
                    },
                    {
                      path: 'profile-privacy',
                      lazy: () => import('./routes/settings/profile-privacy-settings-page').then(convert(queryClient)),
                    },
                    {
                      path: 'subscriptions',
                      lazy: () => import('./routes/settings/subscriptions-page').then(convert(queryClient)),
                    },
                    {
                      path: 'scanner',
                      lazy: () => import('./routes/settings/account-preferences-page').then(convert(queryClient)),
                    },
                    {
                      path: 'workflows',
                      lazy: () => import('./routes/settings/account-preferences-page').then(convert(queryClient)),
                    },
                    {
                      path: 'lynk-preferences',
                      lazy: () => import('./routes/settings/account-preferences-page').then(convert(queryClient)),
                    },
                    {
                      path: 'conrad-phone-number',
                      lazy: () => import('./routes/settings/conrad-phone-number-page').then(convert(queryClient)),
                    },
                    {
                      path: 'account-preferences',
                      lazy: () => import('./routes/settings/account-preferences-page').then(convert(queryClient)),
                    },
                    {
                      path: 'company',
                      lazy: () => import('./routes/settings/enterprise-company-page').then(convert(queryClient)),
                    },
                    {
                      path: 'integrations',
                      lazy: () => import('./routes/settings/integrations-page').then(convert(queryClient)),
                    },
                    {
                      path: 'invite-network',
                      lazy: () => import('./routes/settings/account-preferences-page').then(convert(queryClient)),
                    },
                    {
                      path: 'about-conrad-pro',
                      lazy: () => import('./routes/settings/account-preferences-page').then(convert(queryClient)),
                    },
                    {
                      path: 'terms-of-use',
                      lazy: () => import('./routes/settings/account-preferences-page').then(convert(queryClient)),
                    },
                    {
                      path: 'privacy-policy',
                      lazy: () => import('./routes/settings/account-preferences-page').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  path: paths.admin.path,
                  lazy: () => import('./routes/admin').then(convert(queryClient)),
                },
                {
                  path: paths.support.path,
                  lazy: () => import('./routes/support').then(convert(queryClient)),
                },
                {
                  path: paths.subscriptions.success.path,
                  lazy: () => import('./routes/subscriptions/success').then(convert(queryClient)),
                },
                {
                  path: paths.subscriptions.sessionCanceled.path,
                  lazy: () => import('./routes/subscriptions/session-canceled').then(convert(queryClient)),
                },
                {
                  path: paths.assistant.history.path,
                  lazy: () => import('./routes/assistant/history').then(convert(queryClient)),
                },
                {
                  path: paths.assistant.customize.path,
                  lazy: () => import('./routes/assistant/customize').then(convert(queryClient)),
                },
                {
                  path: paths.leads.detail.path,
                  lazy: () => import('./routes/business/leads/lead-page').then(convert(queryClient)),
                },
                {
                  path: paths.business.path,
                  element: <BusinessLayout />,
                  children: [
                    {
                      index: true,
                      lazy: () => import('./routes/business/business').then(convert(queryClient)),
                    },
                    {
                      path: paths.business.pending.path,
                      lazy: () => import('./routes/business/business-pending').then(convert(queryClient)),
                    },
                    {
                      path: paths.business.singular.path,
                      lazy: () => import('./routes/business/single/index').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  path: paths.business.create.path,
                  element: <BusinessCreateLayout />,
                  children: [
                    {
                      path: paths.business.create.path,
                      lazy: () => import('./routes/business/create/index').then(convert(queryClient)),
                    },
                  ],
                },
              ],
            },
            // Hide Header Layout
            {
              element: <AuthenticatedLayout hideHeader />,
              children: [
                {
                  path: paths.partnerMatch.path,
                  lazy: () => import('./routes/partner-match/list').then(convert(queryClient)),
                },
                {
                  path: paths.network.path,
                  lazy: () => import('./routes/network').then(convert(queryClient)),
                },
                {
                  element: <ChatsLayout />,
                  children: [
                    {
                      path: paths.chats.list.path,
                      lazy: () => import('./routes/chats/list').then(convert(queryClient)),
                      errorElement: <ErrorPage />,
                    },
                    {
                      path: paths.chats.conversation.path,
                      lazy: () => import('./routes/chats/conversation').then(convert(queryClient)),
                      errorElement: <ErrorPage />,
                    },
                    {
                      path: paths.chats.conversation.referral.path,
                      lazy: () => import('./routes/chats/conversation/referral').then(convert(queryClient)),
                      errorElement: <ErrorPage />,
                    },
                    {
                      path: paths.chats.conversation.match.path,
                      lazy: () => import('./routes/chats/conversation/match').then(convert(queryClient)),
                      errorElement: <ErrorPage />,
                    },
                    {
                      path: paths.chats.conversation.network.path,
                      lazy: () => import('./routes/chats/conversation/network').then(convert(queryClient)),
                      errorElement: <ErrorPage />,
                    },
                    {
                      path: paths.chats.conversation.sneak.path,
                      lazy: () => import('./routes/chats/conversation/sneak').then(convert(queryClient)),
                      errorElement: <ErrorPage />,
                    },
                  ],
                },
                /*{
                  path: paths.referrals.list.path,
                  // lazy: () => import('./routes/business/business').then(convert(queryClient)),
                  element: <BusinessLayout />,
                  errorElement: <ErrorPage />,
                  children: [
                    {
                      path: paths.referrals.list.path,
                      // element: <Navigate to={paths.referrals.list.received.path} replace />,
                      lazy: () => import('./routes/business/business').then(convert(queryClient)),
                    },
                    {
                      path: paths.referrals.list.received.path,
                      lazy: () => import('./routes/business/received').then(convert(queryClient)),
                    },
                    {
                      path: paths.referrals.list.shared.path,
                      lazy: () => import('./routes/business/referrals/list/shared').then(convert(queryClient)),
                    },
                    {
                      path: paths.referrals.list.pending.path,
                      lazy: () => import('./routes/business/business-pending').then(convert(queryClient)),
                    },
                  ],
                },*/
              ],
            },
            // Hide Footer Layout
            {
              element: <AuthenticatedLayout hideFooter />,
              children: [
                {
                  path: paths.referrals.getMatched.info.path,
                  lazy: () => import('./routes/business/referrals/get-matched/info').then(convert(queryClient)),
                },
                {
                  path: paths.referrals.getMatched.results.path,
                  lazy: () => import('./routes/business/referrals/get-matched/results').then(convert(queryClient)),
                },
                //
                {
                  path: paths.users.editProfile.path,
                  lazy: () => import('./routes/user/edit/edit-profile-page').then(convert(queryClient)),
                },
                {
                  path: paths.users.editLicenses.path,
                  lazy: () => import('./routes/user/edit/edit-licenses-page').then(convert(queryClient)),
                },
                {
                  path: paths.referrals.detail.referralShare.path,
                  element: <ReceivedReferralSharePage />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: paths.referrals.detail.referral.path,
                  element: <SentReferralPage />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: paths.referrals.send.path,
                  lazy: () => import('./routes/business/referrals/send').then(convert(queryClient)),
                  errorElement: <ErrorPage />,
                },
                {
                  path: paths.users.points.path,
                  element: <Navigate to={paths.home.getHref()} replace />,
                },
                {
                  path: paths.assistant.path,
                  lazy: () => import('./routes/assistant').then(convert(queryClient)),
                },
                {
                  path: paths.assistant.conversation.path,
                  lazy: () => import('./routes/assistant/conversation').then(convert(queryClient)),
                },
              ],
            },
            // Hide Both Layout
            {
              element: <AuthenticatedLayout hideHeader hideFooter />,
              children: [
                {
                  path: paths.invite.path,
                  lazy: () => import('./routes/invite').then(convert(queryClient)),
                },
                {
                  path: paths.feed.post.path,
                  lazy: () => import('./routes/feed/post').then(convert(queryClient)),
                  errorElement: <ErrorPage />,
                },
                {
                  path: paths.feed.comment.path,
                  lazy: () => import('./routes/feed/comment').then(convert(queryClient)),
                  errorElement: <ErrorPage />,
                },
                {
                  path: paths.partnerMatch.preferences.path,
                  lazy: () => import('./routes/partner-match/preferences').then(convert(queryClient)),
                },
                {
                  path: paths.leads.grades.path,
                  lazy: () => import('./routes/business/leads/how-grades-work').then(convert(queryClient)),
                },
              ],
            },
            {
              path: paths.users.completeProfile.path,
              lazy: () => import('./routes/user/edit/complete-profile').then(convert(queryClient)),
            },
            {
              path: paths.partnerMatch.unlock.path,
              lazy: () => import('./routes/partner-match/unlock').then(convert(queryClient)),
            },
            {
              path: paths.recap.path,
              lazy: () => import('./routes/recap').then(convert(queryClient)),
            },
            {
              path: paths.subscriptions.path,
              lazy: () => import('./routes/subscriptions/subscription-page').then(convert(queryClient)),
            },
            {
              path: paths.subscriptions.path,
              lazy: () => import('./routes/subscriptions/subscription-page').then(convert(queryClient)),
            },
          ],
        },
        // Public Pages
        {
          element: (
            <ErrorBoundaryWrapper>
              <PublicLayout />
            </ErrorBoundaryWrapper>
          ),
          children: [
            // unsubscribe only used via email link
            {
              path: paths.users.unsubscribe.path,
              lazy: () => import('./routes/email-actions/unsubscribe').then(convert(queryClient)),
            },
          ],
        },
        {
          path: '/:username',
          element: <UserProfile />,
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.users.publicProfile.singleListing.path,
          lazy: () => import('./routes/user/profile/public/single-listing').then(convert(queryClient)),
        },
        {
          path: paths.users.publicProfile.portfolio.single.path,
          lazy: () => import('./routes/user/profile/public/single-portfolio').then(convert(queryClient)),
        },
        // User Profile (Public & Private) -> Layouts within the pages
        {
          path: '/:username/public',
          element: (
            <PublicProfileLayout profile>
              <Outlet />
            </PublicProfileLayout>
          ),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
          children: [
            {
              path: paths.users.publicProfile.path,
              lazy: () => import('./routes/user/profile/public-user-profile').then(convert(queryClient)),
            },
            {
              path: paths.users.publicProfile.listings.path,
              lazy: () => import('./routes/user/profile/public/listings').then(convert(queryClient)),
            },
            {
              path: paths.users.publicProfile.reviews.path,
              lazy: () => import('./routes/user/profile/public/reviews').then(convert(queryClient)),
            },
            {
              path: paths.users.publicProfile.activity.path,
              lazy: () => import('./routes/user/profile/public/activity').then(convert(queryClient)),
            },
            {
              path: paths.users.publicProfile.portfolio.path,
              lazy: () => import('./routes/user/profile/public/portfolio').then(convert(queryClient)),
            },
          ],
        },
        {
          path: '/:username/message',
          lazy: () => import('./routes/user/message').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          element: <Outlet />,
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
          children: [
            {
              path: paths.marketplace.path,
              lazy: () => import('./routes/marketplace').then(convert(queryClient)),
            },
            {
              path: paths.marketplace.details.path,
              lazy: () => import('./routes/marketplace').then(convert(queryClient)),
            },
          ],
        },
        {
          path: paths.marketplace.details.path,
          lazy: () => import('./routes/marketplace').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.marketplace.filters.path,
          lazy: () => import('./routes/marketplace/filters').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.marketplace.instantMatch.path,
          lazy: () => import('./routes/marketplace/instant-match').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.marketplace.instantMatch.userId.path,
          lazy: () => import('./routes/marketplace/instant-match/user/user-id').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.marketplace.instantMatch.profession.path,
          lazy: () => import('./routes/marketplace/instant-match/profession').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.marketplace.instantMatch.mortgageCalculator.path,
          lazy: () => import('./routes/marketplace/instant-match/mortgage-calculator').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        // Error Page
        {
          path: paths.error.notFound.path,
          lazy: () => import('./routes/error').then(convert(queryClient)),
        },
        // Landing Pages
        {
          path: paths.landing.google.path,
          lazy: () => import('./routes/landing/google-ads').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.google.financingAd.path,
          lazy: () => import('./routes/landing/google-financing-ad').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.google.closingFrustrations.path,
          lazy: () => import('./routes/landing/google-closing-frustrations').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.google.inventoryCompetition.path,
          lazy: () => import('./routes/landing/google-inventory-competition').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.google.postPurchaseRegretMatch.path,
          lazy: () => import('./routes/landing/google-post-purchase-regret-match').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.google.realEstateMatch.path,
          lazy: () => import('./routes/landing/google-real-estate-match').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.meta.path,
          lazy: () => import('./routes/landing/meta-ads').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.vsl.path,
          lazy: () => import('./routes/landing/vsl').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.affordable.path,
          lazy: () => import('./routes/landing/affordable').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.approved.path,
          lazy: () => import('./routes/landing/approved').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.findAnAgent.path,
          lazy: () => import('./routes/landing/find-an-agent').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },

        // Team Pages
        {
          path: paths.landing.douglasElliman.path,
          lazy: () => import('./routes/landing/douglas-elliman').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.reTeam.taylorKaneGroup.path,
          lazy: () => import('./routes/landing/team-pages/taylor-kane-group').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.reTeam.jerrySkefos.path,
          lazy: () => import('./routes/landing/team-pages/jerry-skefos').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.reTeam.lanierGroup.path,
          lazy: () => import('./routes/landing/team-pages/lanier-group').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.reTeam.kaseRealestate.path,
          lazy: () => import('./routes/landing/team-pages/kase-realestate').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.reTeam.realtyOneGroup.path,
          lazy: () => import('./routes/landing/team-pages/realty-one-group').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.reTeam.theCelestePandhiTeam.path,
          lazy: () => import('./routes/landing/team-pages/the-celeste-pandhi-team').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.reTeam.sell.path,
          lazy: () => import('./routes/landing/team-pages/sell').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.reTeam.buy.path,
          lazy: () => import('./routes/landing/team-pages/buy').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        // Home Page
        {
          path: paths.root.path,
          lazy: () => import('./routes/index').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        // Blog Pages
        {
          path: paths.blog.list.path,
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
          lazy: () => import('./routes/blog/list').then(convert(queryClient)),
        },
        {
          path: paths.blog.post.path,
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
          lazy: () => import('./routes/blog/post').then(convert(queryClient)),
        },
        {
          path: paths.blog.studio.path,
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
          lazy: () => import('./routes/blog/studio').then(convert(queryClient)),
        },
        // Fallback route -> Redirect to home
        {
          path: '*',
          element: <Navigate to={paths.root.getHref()} />,
        },
      ],
    },
  ])
}

export const AppRouter = () => {
  const queryClient = useQueryClient()
  const router = useMemo(() => createAppRouter(queryClient), [queryClient])

  return <RouterProvider router={router} />
}
